import { debounce } from 'lodash/fp';
import { ChangeEvent, Component, FocusEvent, ForwardedRef, forwardRef, KeyboardEvent, MouseEvent } from 'react';
import { InputValue } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { TextInputProps } from '../TextInput';
import { isValidSearchTerm } from './helpers';
import { SearchTextInput } from './style';

export interface SearchInputProps extends TextInputProps {
    onSearch: (value: string) => void;
    selectedLabel?: string;
    forwardedRef?: ForwardedRef<HTMLInputElement>;
}

class SearchInputComponent extends Component<SearchInputProps> {
    private searchInputElement?: HTMLInputElement;

    state = {
        isFocused: false,
        isSearchWaiting: false,
    };

    componentWillUnmount() {
        this.debounceSearch.cancel();
    }

    debounceSearch = debounce(1000, () => {
        if (this.props.value) {
            this.props.onSearch(this.props.value.toString());
        }
        this.setState({ isSearchWaiting: false });
    });

    handleInputChange = (event: ChangeEvent<HTMLInputElement> | MouseEvent, value: InputValue) => {
        const newValue = value ?? '';

        this.props.onChange?.(event, newValue);

        if (newValue.toString().length === 0) {
            this.props.onClear?.();
        } else if (isValidSearchTerm(newValue)) {
            this.debounceSearch();
            this.setState({ isSearchWaiting: true });
        } else {
            this.debounceSearch.cancel();
            this.setState({ isSearchWaiting: false });
        }
    };

    handleInputFocus = (event: FocusEvent<HTMLInputElement>) => {
        const { value = '' } = this.props;

        this.setState({ isFocused: true });

        if (this.searchInputElement) {
            this.searchInputElement.focus();
            this.searchInputElement.setSelectionRange(0, value.toString().length);
        }

        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    };

    handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
        this.setState({ isFocused: false });

        if (this.props.onBlur) {
            this.props.onBlur(event);
        }
    };

    handleClearClick = () => {
        this.props.onClear?.();
    };

    handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (this.props.onKeyDown) {
            this.props.onKeyDown(event);
        }
    };

    searchInputElementRef = (c: HTMLInputElement) => {
        this.searchInputElement = c;

        if (typeof this.props.forwardedRef === 'function') {
            this.props.forwardedRef(c);
        } else if (this.props.forwardedRef) {
            this.props.forwardedRef.current = c;
        }
    };

    render() {
        const { placeholder, size, value, selectedLabel, loading, clearable, onSearch, ...other } = this.props;

        // Determine the value to display based on controlled vs uncontrolled mode
        const inputValue = selectedLabel && !this.state.isFocused ? selectedLabel : value;

        // If the value is empty and the selectedLabel is not set, show the placeholder
        const showPlaceholder = !inputValue && !selectedLabel;

        const inputProps = {
            type: 'search',
            ...other,
            loading: this.state.isSearchWaiting || loading,
            placeholder: showPlaceholder ? placeholder : '',
            value: inputValue,
            ref: this.searchInputElementRef,
            onChange: this.handleInputChange,
            onKeyDown: this.handleInputKeyDown,
            onFocus: this.handleInputFocus,
            onBlur: this.handleInputBlur,
            indicativeIcon: IconName.Search,
            clearable: clearable || !!selectedLabel,
            onClear: this.handleClearClick,
            size,
        };

        return <SearchTextInput {...inputProps} />;
    }
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>((props, ref) => (
    <SearchInputComponent {...props} forwardedRef={ref} />
));
